/**
 * payroll-schema.ts — สรุปเงินเดือนต่องวด/หน่วยงาน (CONTEXT §5.5, T064)
 * Phase 1: สรุประดับงวด ไม่ทำ HR/สลิปรายบุคคล
 *
 * กติกาบัญชี (สำคัญ):
 * - Gross pay = salary+wage+ot+bonus+commission+allowance+welfare
 * - Employer contributions (SSO/WCF) = expense เพิ่มของกิจการ → เข้า P&L
 * - Employee deductions (SSO/tax/กยศ./อื่น) = หักจาก gross ของพนักงาน
 *   ไม่ใช่ expense เพิ่มของกิจการ → **ไม่นับซ้ำใน P&L**
 * - Net pay = gross − employee deductions
 * - P&L expense (ต้นทุนพนักงานต่อกิจการ) = gross + employer contributions
 */
import { z } from 'zod';
import { moneyString } from './schemas.js';
import { addMoney, subtractMoney, moneyToString, type MoneyInput } from './money.js';

/** money optional (default 0 ตอน compute) */
const money = moneyString.optional();

export const payrollSummaryInput = z.object({
  costCenter: z.string().trim().max(200).optional(),
  periodYear: z.coerce.number().int().min(2000).max(2100),
  periodMonth: z.coerce.number().int().min(1).max(12),
  headcount: z.coerce.number().int().min(0).optional(),

  // gross components
  salary: money,
  wage: money,
  ot: money,
  bonus: money,
  commission: money,
  allowance: money,
  welfare: money,

  // employer contributions (expense ของกิจการ)
  employerSso: money,
  employerWcf: money,

  // employee deductions (หักจากพนักงาน — ไม่ใช่ expense เพิ่ม)
  employeeSso: money,
  employeeTax: money,
  employeeLoanGsl: money,
  otherDeductions: money,

  /** ถ้าส่งมา จะถูก validate ว่าตรงกับ gross − deductions; ถ้าไม่ส่งจะ compute ให้ */
  netPay: money,

  reason: z.string().trim().max(1000).optional(),
});
export type PayrollSummaryInput = z.infer<typeof payrollSummaryInput>;

export const payrollSummaryPatch = payrollSummaryInput.partial();
export type PayrollSummaryPatch = z.infer<typeof payrollSummaryPatch>;

export interface PayrollTotals {
  grossPay: string;
  employerContributions: string;
  employeeDeductions: string;
  netPay: string;
  /** ต้นทุนพนักงานที่กระทบ P&L = gross + employer (ไม่รวม employee deductions ซ้ำ) */
  pnlExpense: string;
}

const g = (v: MoneyInput | undefined): MoneyInput => v ?? '0';

/**
 * คำนวณยอดรวม payroll ด้วย Decimal (ห้าม float)
 * encode กติกา employer=expense, employee deductions ไม่นับซ้ำ
 */
export function computePayrollTotals(p: {
  salary?: string | undefined;
  wage?: string | undefined;
  ot?: string | undefined;
  bonus?: string | undefined;
  commission?: string | undefined;
  allowance?: string | undefined;
  welfare?: string | undefined;
  employerSso?: string | undefined;
  employerWcf?: string | undefined;
  employeeSso?: string | undefined;
  employeeTax?: string | undefined;
  employeeLoanGsl?: string | undefined;
  otherDeductions?: string | undefined;
}): PayrollTotals {
  const grossPay = addMoney(
    g(p.salary),
    g(p.wage),
    g(p.ot),
    g(p.bonus),
    g(p.commission),
    g(p.allowance),
    g(p.welfare),
  );
  const employerContributions = addMoney(g(p.employerSso), g(p.employerWcf));
  const employeeDeductions = addMoney(
    g(p.employeeSso),
    g(p.employeeTax),
    g(p.employeeLoanGsl),
    g(p.otherDeductions),
  );
  const netPay = subtractMoney(grossPay, employeeDeductions);
  const pnlExpense = grossPay.plus(employerContributions);
  return {
    grossPay: moneyToString(grossPay),
    employerContributions: moneyToString(employerContributions),
    employeeDeductions: moneyToString(employeeDeductions),
    netPay: moneyToString(netPay),
    pnlExpense: moneyToString(pnlExpense),
  };
}
